import type { ReactNode } from "react";
import { useOverflowList } from "../hooks/use-overflow-list";
import type { ClassNameProps } from "../types";
import { cn } from "../utils";

/** Props Interface */
export interface OverflowListProps<T> extends ClassNameProps {
	items: T[];
	renderItem: (item: T, index: number, isLast: boolean) => ReactNode;
	renderOverflowItem: (payload: { hiddenItems: T[]; overflowCount: number }) => ReactNode;
	gap?: number;
	justify?: "start" | "end";
	recalculate?: boolean;
}

export function OverflowList<T>({
	items,
	renderItem,
	renderOverflowItem,
	gap = 8,
	justify = "start",
	recalculate = false,
	className,
}: OverflowListProps<T>) {
	/** Hooks */
	const { containerRef, visibleCount, hiddenCount } = useOverflowList(items.length, recalculate);

	/** Computed */
	const visibleItems = items.slice(0, visibleCount);
	const hiddenItems = items.slice(visibleCount);

	/** Render */
	return (
		<div
			ref={containerRef}
			style={{ position: "relative", overflow: "hidden" }}
		>
			<div
				style={{
					display: "flex",
					gap,
					justifyContent: justify === "end" ? "flex-end" : "flex-start",
				}}
				className={cn(className)}
			>
				{visibleItems.map((item, i) => renderItem(item, i, i === items.length - 1))}
				{hiddenCount > 0 && renderOverflowItem({ hiddenItems, overflowCount: hiddenCount })}
			</div>
			<div
				data-overflow-measure=""
				aria-hidden="true"
				style={{
					position: "absolute",
					top: 0,
					left: "-9999px",
					display: "flex",
					gap,
					pointerEvents: "none",
					visibility: "hidden",
				}}
			>
				{items.map((item, i) => (
					<div
						key={i}
						data-measure-item=""
						style={{ flexShrink: 0 }}
					>
						{renderItem(item, i, i === items.length - 1)}
					</div>
				))}
				<div
					data-measure-overflow=""
					style={{ flexShrink: 0 }}
				>
					{renderOverflowItem({ hiddenItems: items, overflowCount: items.length })}
				</div>
			</div>
		</div>
	);
}
